/**
 * Retrieve Wikimedia Tool
 *
 * Downloads and processes specific image from Wikimedia Commons.
 * Performs:
 * - Image details retrieval
 * - Image download
 * - Asset storage with deduplication
 * - Description generation
 *
 * Based on WikimediaSearcher from big-slide-gen.
 */
import { ToolDescription, safeParseToolParams } from "../../../../base/toolDescription";
import { ToolBase } from "../../../../base/toolBase";
import { generateId } from "../../../../../utils/idGenerator";
import { ContextResolver } from "../../../../../core/contextResolver";
import { WIKIMEDIA_API_URL, WIKIMEDIA_USER_AGENT } from "../../config";
import { resizeImageForVision } from "../../utils/imageResize";
import { simpleHash, findExistingAsset, registerContentHash } from "../../utils/deduplicationCache";
import { RetrieveWikimediaParamsSchema, } from "./retrieveWikimedia.schema";
// ═══════════════════════════════════════════════════════════════════════════
// IMAGE UTILITIES
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Strip HTML tags from string.
 */
function stripHtml(html) {
    return html.replace(/<[^>]*>/g, '').trim();
}
/**
 * Get MIME type from URL.
 */
function getMimeTypeFromUrl(url) {
    const ext = url.split('.').pop()?.toLowerCase() || '';
    switch (ext) {
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        case 'gif':
            return 'image/gif';
        case 'webp':
            return 'image/webp';
        case 'svg':
            return 'image/svg+xml';
        default:
            return 'image/jpeg';
    }
}
/**
 * Generate description from metadata.
 */
function generateDescription(fileName, description, dimensions) {
    // Clean up filename for display
    const cleanName = fileName
        .replace(/_/g, ' ')
        .replace(/\.\w+$/, '')
        .slice(0, 50);
    // Use Wikimedia description if available, otherwise generate from filename
    if (description && description.length > 10) {
        const shortDesc = description.slice(0, 80);
        return `${shortDesc}${description.length > 80 ? '...' : ''} (${dimensions.width}x${dimensions.height})`;
    }
    return `${cleanName} from Wikimedia Commons (${dimensions.width}x${dimensions.height})`;
}
// ═══════════════════════════════════════════════════════════════════════════
// WIKIMEDIA API CLIENT
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Get full details for a specific image file.
 */
async function getImageDetails(fileName) {
    try {
        // Ensure File: prefix
        let queryTitle = fileName;
        if (!queryTitle.startsWith('File:')) {
            queryTitle = `File:${queryTitle}`;
        }
        const params = new URLSearchParams({
            action: 'query',
            titles: queryTitle,
            prop: 'imageinfo',
            iiprop: 'url|size|extmetadata',
            format: 'json',
            origin: '*',
        });
        const response = await fetch(`${WIKIMEDIA_API_URL}?${params}`, {
            method: 'GET',
            headers: {
                'Api-User-Agent': WIKIMEDIA_USER_AGENT,
            },
        });
        if (!response.ok) {
            console.warn(`[RetrieveWikimedia] API returned ${response.status}`);
            return null;
        }
        const data = await response.json();
        const pages = data.query?.pages || {};
        // Get first page
        const pageData = Object.values(pages)[0];
        if (!pageData || !pageData.imageinfo?.[0]) {
            return null;
        }
        const info = pageData.imageinfo[0];
        const extmetadata = info.extmetadata || {};
        return {
            fileName: pageData.title.replace(/^File:/, ''),
            url: info.url,
            width: info.width || 0,
            height: info.height || 0,
            sizeBytes: info.size || 0,
            description: stripHtml(extmetadata.ImageDescription?.value || ''),
            license: extmetadata.LicenseShortName?.value || 'Unknown',
        };
    }
    catch (error) {
        console.error('[RetrieveWikimedia] Failed to get image details:', error);
        return null;
    }
}
/**
 * Download image and convert to base64.
 */
async function downloadImage(url) {
    try {
        const response = await fetch(url, {
            headers: {
                'User-Agent': WIKIMEDIA_USER_AGENT,
            },
        });
        if (!response.ok) {
            console.warn(`[RetrieveWikimedia] Download failed: ${response.status}`);
            return null;
        }
        const contentType = response.headers.get('content-type') || getMimeTypeFromUrl(url);
        const arrayBuffer = await response.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        // Convert to base64
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        return { base64, mimeType: contentType.split(';')[0].trim() };
    }
    catch (error) {
        console.error('[RetrieveWikimedia] Download error:', error);
        return null;
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════
const RETRIEVE_WIKIMEDIA_SYSTEM_PROMPT = `### retrieveWikimedia
Download a specific image from Wikimedia Commons.

**When to use:**
- After searchWikimedia found desired images
- To get actual image for insertion into slides

**Call AFTER searchWikimedia** to get the fileName from search results.

\`\`\`json
{
  "fileName": "File:Satya_Nadella.jpg",
  "requirementId": "B"
}
\`\`\`

Returns: wikimedia_xxx asset ID for use with insertArtifacts.`;
// ═══════════════════════════════════════════════════════════════════════════
// RETRIEVE WIKIMEDIA TOOL
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Tool for downloading and processing Wikimedia images.
 */
export class RetrieveWikimediaTool extends ToolBase {
    constructor() {
        super();
        this.name = "retrieveWikimedia";
        // System prompt description
        this.systemPromptDescription = RETRIEVE_WIKIMEDIA_SYSTEM_PROMPT;
        // Streaming operation fields
        this.fieldsToCheck = [
            { name: 'uiDescription', type: 'string', list_status: false, order: 0 },
            { name: 'fileName', type: 'string', list_status: false, order: 1 },
            { name: 'requirementId', type: 'string', list_status: false, order: 2 },
        ];
        this.listsToCheck = [];
        this.description = new ToolDescription("retrieveWikimedia", `Download and process a specific image from Wikimedia Commons.

**Purpose:** Retrieve an image found via searchWikimedia.

**Parameters:**
- fileName: File name from search result (e.g., "Satya_Nadella.jpg")
- requirementId: The requirement this asset fulfills

**Example:**
\`\`\`json
{
  "fileName": "Satya_Nadella_World_Economic_Forum_2024.jpg",
  "requirementId": "B"
}
\`\`\`

**Returns:**
- assetId: Unique ID (e.g., "wikimedia_abc12")
- note: Generated description
- metadata: Image details (dimensions, license, etc.)
- alreadyExists: True if this exact asset was already stored`, RetrieveWikimediaParamsSchema);
    }
    async execute(parameters) {
        const parseResult = safeParseToolParams(RetrieveWikimediaParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }
        const params = parseResult.data;
        console.log(`[RetrieveWikimediaTool] Retrieving "${params.fileName}"`);
        try {
            // 1. Get image details from Wikimedia API
            const details = await getImageDetails(params.fileName);
            if (!details) {
                return {
                    success: false,
                    assetId: '',
                    requirementId: params.requirementId,
                    note: '',
                    metadata: {
                        type: 'image',
                        source: 'wikimedia',
                        fileName: params.fileName,
                        dimensions: { width: 0, height: 0 },
                        license: 'Unknown',
                        description: '',
                    },
                    alreadyExists: false,
                    error: `Image not found: ${params.fileName}`,
                };
            }
            // 2. Download the image
            console.log(`[RetrieveWikimediaTool] Downloading from ${details.url}`);
            const downloaded = await downloadImage(details.url);
            if (!downloaded) {
                return {
                    success: false,
                    assetId: '',
                    requirementId: params.requirementId,
                    note: '',
                    metadata: {
                        type: 'image',
                        source: 'wikimedia',
                        fileName: details.fileName,
                        dimensions: { width: details.width, height: details.height },
                        license: details.license,
                        description: details.description,
                    },
                    alreadyExists: false,
                    error: `Failed to download image from ${details.url}`,
                };
            }
            // 3. Check for deduplication
            const contentHash = simpleHash(downloaded.base64);
            const existingId = findExistingAsset(contentHash);
            if (existingId) {
                console.log(`[RetrieveWikimediaTool] ✅ Asset already exists: ${existingId}`);
                const contextResolver = ContextResolver.getInstance();
                const existing = contextResolver.resolve(existingId);
                const existingMeta = existing?.content?.metadata || {
                    type: 'image',
                    source: 'wikimedia',
                    fileName: details.fileName,
                    dimensions: { width: details.width, height: details.height },
                    license: details.license,
                    description: details.description,
                };
                return {
                    success: true,
                    assetId: existingId,
                    requirementId: params.requirementId,
                    note: generateDescription(details.fileName, details.description, { width: details.width, height: details.height }),
                    metadata: existingMeta,
                    alreadyExists: true,
                };
            }
            // 4. Store new asset
            const contextResolver = ContextResolver.getInstance();
            const assetId = generateId('wikimedia');
            // Convert (if SVG) and resize image if needed for Anthropic's vision API
            const resized = await resizeImageForVision(downloaded.base64, downloaded.mimeType);
            if (resized.wasConverted) {
                console.log(`[RetrieveWikimediaTool] SVG converted to PNG for vision model compatibility`);
            }
            if (resized.wasResized) {
                console.log(`[RetrieveWikimediaTool] Image resized for vision model compatibility`);
            }
            // Store image in image cache (using potentially converted/resized version)
            contextResolver.storeImage(assetId, resized.base64, resized.mimeType, {
                source: 'wikimedia',
                fileName: details.fileName,
                dimensions: { width: details.width, height: details.height },
                originalFormat: downloaded.mimeType,
                wasConverted: resized.wasConverted,
                wasResized: resized.wasResized,
            });
            // Store metadata
            const metadata = {
                type: 'image',
                source: 'wikimedia',
                fileName: details.fileName,
                dimensions: { width: details.width, height: details.height },
                license: details.license,
                description: details.description,
            };
            contextResolver.storeWithId(assetId, {
                assetType: 'wikimedia_image',
                fileName: details.fileName,
                metadata,
                requirementId: params.requirementId,
                _imageId: assetId, // Include image ID so extractContextImageIds can find it
            });
            // Register for deduplication
            registerContentHash(contentHash, assetId);
            const note = generateDescription(details.fileName, details.description, { width: details.width, height: details.height });
            console.log(`[RetrieveWikimediaTool] ✅ Retrieved asset: ${assetId} (${details.width}x${details.height})`);
            return {
                success: true,
                assetId,
                requirementId: params.requirementId,
                note,
                metadata,
                alreadyExists: false,
            };
        }
        catch (error) {
            console.error("[RetrieveWikimediaTool] ❌ Error:", error);
            return {
                success: false,
                assetId: '',
                requirementId: params.requirementId,
                note: '',
                metadata: {
                    type: 'image',
                    source: 'wikimedia',
                    fileName: params.fileName,
                    dimensions: { width: 0, height: 0 },
                    license: 'Unknown',
                    description: '',
                },
                alreadyExists: false,
                error: `Retrieval failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
            };
        }
    }
}
